<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <h5 class="mb-25">
          <feather-icon
            icon="Columns"
            size="18"
            class="mr-50 text-primary"
          />
          {{ $t('modules.products.import.steps.mapping.title') }}
        </h5>
        <p class="text-muted mb-2">
          {{ $t('modules.products.import.steps.mapping.description') }}
        </p>

        <!-- Legend -->
        <div class="d-flex align-items-center mb-2">
          <b-badge
            variant="light-danger"
            class="mr-50"
          >
            {{ $t('modules.products.import.steps.mapping.required') }}
          </b-badge>
          <b-badge
            variant="light-secondary"
          >
            {{ $t('modules.products.import.steps.mapping.optional') }}
          </b-badge>
        </div>

        <!-- Mapping rows -->
        <div
          v-for="field in expectedFields"
          :key="field.key"
          class="mapping-row mb-1 p-1 rounded border"
          :class="{ 'border-danger': field.required && !mapping[field.key] }"
        >
          <b-row align-v="center">
            <!-- Expected field name -->
            <b-col
              cols="12"
              md="4"
              class="mb-75 mb-md-0"
            >
              <div class="d-flex align-items-center">
                <b-badge
                  :variant="field.required ? 'light-danger' : 'light-secondary'"
                  class="mr-50 text-nowrap"
                >
                  {{ field.required ? $t('modules.products.import.steps.mapping.required') : $t('modules.products.import.steps.mapping.optional') }}
                </b-badge>
                <div>
                  <p class="mb-0 font-weight-bold">
                    {{ $t(`modules.products.import.fields.${field.key}`) }}
                  </p>
                  <small
                    v-if="field.hint"
                    class="text-muted"
                  >
                    {{ $t(`modules.products.import.fields_hints.${field.key}`) }}
                  </small>
                </div>
              </div>
            </b-col>

            <!-- Arrow -->
            <b-col
              cols="12"
              md="1"
              class="text-center d-none d-md-block"
            >
              <feather-icon
                :icon="$i18n.locale === 'ar' ? 'ArrowLeftIcon' : 'ArrowRightIcon'"
                class="text-muted"
              />
            </b-col>

            <!-- Column selector -->
            <b-col
              cols="12"
              md="7"
            >
              <v-select
                v-model="mapping[field.key]"
                :options="columnOptions"
                :reduce="opt => opt.value"
                :placeholder="$t('modules.products.import.steps.mapping.select_column')"
                :clearable="!field.required"
                class="w-100"
              >
                <template #option="{ label, sampleValues }">
                  <div>
                    <span class="font-weight-bold">{{ label }}</span>
                    <br>
                    <small class="text-muted">
                      {{ $t('modules.products.import.steps.mapping.example') }}:
                      <span
                        v-for="(val, i) in sampleValues.slice(0, 3)"
                        :key="i"
                      >
                        <b-badge
                          variant="light-secondary"
                          class="mr-25"
                        >{{ val }}</b-badge>
                      </span>
                    </small>
                  </div>
                </template>
                <template #selected-option="{ label, sampleValues }">
                  <span>{{ label }}</span>
                  <small
                    v-if="sampleValues && sampleValues.length"
                    class="text-muted ml-50"
                  >
                    — {{ sampleValues[0] }}
                  </small>
                </template>
              </v-select>
            </b-col>
          </b-row>
        </div>

        <!-- Validation errors -->
        <b-alert
          v-if="validationError"
          show
          variant="warning"
          class="mt-1"
        >
          <feather-icon
            icon="AlertTriangleIcon"
            class="mr-50"
          />
          {{ validationError }}
        </b-alert>
      </b-card-body>
    </b-card>

    <!-- Actions -->
    <div class="d-flex justify-content-between mt-1">
      <b-button
        variant="outline-secondary"
        @click="$emit('back')"
      >
        <feather-icon
          :icon="$i18n.locale === 'ar' ? 'ArrowRightIcon' : 'ArrowLeftIcon'"
          class="mr-50"
        />
        {{ $t('modules.products.import.actions.back') }}
      </b-button>

      <b-button
        variant="primary"
        :disabled="isLoading"
        @click="onClickNext"
      >
        <b-spinner
          v-if="isLoading"
          small
          class="mr-50"
        />
        <span v-else>
          {{ isLoading ? $t('modules.products.import.actions.loading_preview') : $t('modules.products.import.actions.preview') }}
          <feather-icon
            icon="EyeIcon"
            class="ml-50"
          />
        </span>
      </b-button>
    </div>
  </div>
</template>

<script>
import {
  BBadge, BButton, BCard, BCardBody, BCol, BRow, BAlert, BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { ref, computed, watch } from 'vue'

const EXPECTED_FIELDS = [
  { key: 'slug', required: false, hint: true },
  { key: 'name', required: true, hint: true },
  { key: 'description', required: false, hint: false },
  { key: 'price', required: true, hint: true },
  { key: 'sale_price', required: false, hint: false },
  { key: 'sku', required: false, hint: true },
  { key: 'quantity', required: false, hint: false },
  { key: 'category', required: false, hint: true },
  { key: 'weight', required: false, hint: false },
  { key: 'images', required: false, hint: true },
  { key: 'is_active', required: false, hint: true },
]

// Ordered candidate column names per field (first match wins, case-insensitive)
const DEFAULT_COLUMN_CANDIDATES = {
  slug: ['URL Slug', 'Slug', 'URL', 'Product URL', 'Handle'],
  name: ['Name (EN)', 'Name (AR)', 'Name'],
  description: ['Description (HTML) (EN)', 'Description (HTML) (AR)', 'Description'],
  price: ['Variant Price', 'Price'],
  sale_price: ['Variant Sale Price', 'Sale Price'],
  sku: ['SKU', 'sku'],
  quantity: ['Variant Qty', 'Qty', 'Quantity'],
  category: ['Category Name (EN)', 'Category Name (AR)', 'Category Name', 'Category'],
  weight: ['Weight Amount (g)', 'Weight Amount', 'Weight'],
  images: ['Images'],
  is_active: ['Published', 'Status'],
}

const resolveDefaults = columns => {
  const result = {}
  const lowerNames = columns.map(c => (c.name || '').toLowerCase())

  Object.entries(DEFAULT_COLUMN_CANDIDATES).forEach(([field, candidates]) => {
    for (const candidate of candidates) {
      const idx = lowerNames.indexOf(candidate.toLowerCase())
      if (idx !== -1) {
        result[field] = columns[idx].index
        break
      }
    }
  })
  return result
}

export default {
  components: {
    BCard,
    BCardBody,
    BBadge,
    BButton,
    BCol,
    BRow,
    BAlert,
    BSpinner,
    vSelect,
  },

  props: {
    columns: {
      type: Array,
      default: () => [],
      // [{ index: 0, name: 'Column A', sample_values: ['foo', 'bar'] }]
    },
    value: {
      type: Object,
      default: () => ({}),
      // { name: 0, price: 2, ... }
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['input', 'next', 'back'],

  setup(props, { emit }) {
    const mapping = ref({ ...props.value })
    const validationError = ref(null)

    // Pre-select defaults as soon as columns are available
    watch(
      () => props.columns,
      cols => {
        if (!cols || !cols.length) return
        const defaults = resolveDefaults(cols)
        // Only fill fields that haven't already been chosen by the user
        Object.entries(defaults).forEach(([field, colIndex]) => {
          if (mapping.value[field] === undefined || mapping.value[field] === null) {
            mapping.value[field] = colIndex
          }
        })
      },
      { immediate: true },
    )

    watch(mapping, val => emit('input', { ...val }), { deep: true })

    const columnOptions = computed(() => props.columns.map(col => ({
      label: col.name || `Column ${col.index + 1}`,
      value: col.index,
      sampleValues: col.sample_values || [],
    })))

    const expectedFields = EXPECTED_FIELDS

    const validate = () => {
      const requiredMissing = EXPECTED_FIELDS
        .filter(f => f.required && (mapping.value[f.key] === undefined || mapping.value[f.key] === null))
        .map(f => f.key)

      if (requiredMissing.length) {
        validationError.value = `Please map required fields: ${requiredMissing.join(', ')}`
        return false
      }
      validationError.value = null
      return true
    }

    const onClickNext = () => {
      if (!validate()) return
      emit('next', { ...mapping.value })
    }

    return {
      mapping,
      validationError,
      columnOptions,
      expectedFields,
      onClickNext,
    }
  },
}
</script>

<style lang="scss" scoped>
.mapping-row {
  background-color: #f8f8f8;
  transition: border-color 0.15s ease;

  &:hover {
    background-color: #f2f0ff;
  }
}

.dark-layout .mapping-row {
  background-color: #283046;

  &:hover {
    background-color: #2d3659;
  }
}
</style>
